<template>
  <div class="admins-access">
    <div class="admins-access__intro">
      <h3 class="admins-access__title">Admins &amp; access</h3>
      <p class="admins-access__help">
        <strong>Admin</strong> lets a person manage company settings, PSA rules and people,
        and see the Company overview in Ask. The <strong>tenant owner</strong> is the one admin who can
        make people admins or remove admin; they are always an admin, and only Teamwrkr can transfer ownership. <strong>Financial access</strong> is a separate grant: it lets
        a person see financial data (agreements, billing) in Ask. Admin does not include financial access.
      </p>
      <p class="admins-access__help">
        A company always needs at least one admin who can sign in, so the last one can't be removed. Add another admin first.
        Only the tenant owner (or Teamwrkr) can grant or remove financial access.
        Every change is emailed to the person affected and the company's tenant owner.
      </p>
    </div>

    <div v-if="!isLoading && !loadError && !hasOwner" class="admins-access__message admins-access__message--notice">
      <i class="mdi mdi-information-outline"></i> No tenant owner — ask Teamwrkr to set one. Until then, only Teamwrkr can change who is an admin.
    </div>
    <!-- The owner is Company.adminId even before they sign in; they may not be in the list yet. -->
    <div v-else-if="!isLoading && !loadError && owner && !owner.isSignedIn" class="admins-access__message admins-access__message--notice">
      <i class="mdi mdi-information-outline"></i> Tenant owner: {{ formatName(owner) }} (not signed in yet). Until they sign in, only Teamwrkr can change who is an admin.
    </div>

    <!-- Rendered outside the loading/error/table branches so a failed change is reported even
         when the re-sync that follows it fails too. -->
    <div v-if="actionError" class="admins-access__message admins-access__message--error">
      <i class="mdi mdi-alert-circle"></i> {{ actionError }}
    </div>

    <div v-if="isLoading" class="tw-state">
      <b-loading :is-full-page="false" :active="true" />
      <p>Loading people...</p>
    </div>

    <div v-else-if="loadError" class="tw-state tw-state--error">
      <p>{{ loadError }}</p>
    </div>

    <div v-else>
      <b-table
        :data="members"
        :striped="true"
        :hoverable="true"
        :paginated="members.length > 10"
        :per-page="10"
        class="admins-access__table"
      >
        <template #empty>
          <div class="empty-msg">No people to display.</div>
        </template>

        <b-table-column field="name" label="Name" v-slot="props">
          {{ formatName(props.row) }}
          <span v-if="isSelf(props.row)" class="admins-access__you">(you)</span>
          <span v-if="isOwner(props.row)" class="admins-access__owner-badge">Tenant owner</span>
          <span v-if="isOwner(props.row) && owner && !owner.isSignedIn" class="admins-access__you">(not signed in yet)</span>
        </b-table-column>

        <b-table-column field="email" label="Email" v-slot="props">
          {{ props.row.email || '—' }}
        </b-table-column>

        <b-table-column field="isAdmin" label="Admin" v-slot="props">
          <b-tooltip
            :active="Boolean(adminLockReason(props.row))"
            :label="adminLockReason(props.row) || ''"
            position="is-left"
            multilined
          >
            <b-switch
              :model-value="props.row.isAdmin === true"
              :disabled="isBusy(props.row) || Boolean(adminLockReason(props.row))"
              @update:modelValue="value => setAdmin(props.row, value)"
            />
          </b-tooltip>
        </b-table-column>

        <b-table-column field="permissionLevel" label="Financial access" v-slot="props">
          <b-tooltip
            :active="!canManageFinancialAccess"
            label="Only the tenant owner (or Teamwrkr) can change financial access."
            position="is-left"
            multilined
          >
            <b-switch
              :model-value="props.row.permissionLevel === 'admin'"
              :disabled="!canManageFinancialAccess || isBusy(props.row)"
              @update:modelValue="value => setFinancialAccess(props.row, value)"
            />
          </b-tooltip>
        </b-table-column>
      </b-table>
    </div>
  </div>
</template>

<script>
import { useUserStore } from '@/stores/userStore'
import { mapState } from 'pinia'

export default {
  name: 'AdminsAccess',
  props: {
    // Same axios config (Authorization from the teamwrkr-token cookie) the Company page uses;
    // main.js's axios interceptor normalizes the token on every request.
    config: {
      type: Object,
      required: true,
    },
    // The company whose page is open. The backend honors it for super users only (they manage
    // any company's admins); everyone else always gets their own company.
    companyId: {
      type: String,
      default: null,
    },
  },
  emits: ['self-demoted'],
  data() {
    return {
      members: [],
      isLoading: false,
      loadError: null,
      actionError: null,
      busyIds: [],
      // Usable admins (can sign in) as counted by the server — the same number its last-admin
      // guard enforces. Falls back to the visible admin rows if the server didn't send it.
      serverAdminCount: null,
      // spec-tenant-owner: the owner (Company.adminId) and whether this viewer may grant/revoke
      // admin, both from the server (?withMeta=1). The server re-checks every change.
      ownerId: null,
      // { id, firstName, lastName, email, isSignedIn } — sent even when the owner hasn't signed in.
      owner: null,
      serverCanManageAdminGrants: null,
    }
  },
  computed: {
    ...mapState(useUserStore, ['user']),
    adminCount() {
      if (typeof this.serverAdminCount === 'number') return this.serverAdminCount
      return this.members.filter(member => member.isAdmin === true).length
    },
    // Mirrors the backend's canManageAdminGrants: super users, or this company's tenant owner.
    canManageAdminGrants() {
      if (typeof this.serverCanManageAdminGrants === 'boolean') return this.serverCanManageAdminGrants
      return this.user?.isSuperUser === true
    },
    // "No tenant owner" only when Company.adminId is null.
    hasOwner() {
      return Boolean(this.ownerId) || this.members.some(member => member.isTenantOwner === true)
    },
    // Mirrors the backend's canManageFinancialAccess (which re-checks every change): owner-only,
    // the same rule as granting admin (Access & Onboarding Model v1).
    canManageFinancialAccess() {
      return this.canManageAdminGrants
    },
  },
  mounted() {
    this.fetchMembers()
  },
  methods: {
    async fetchMembers() {
      this.isLoading = true
      this.loadError = null
      try {
        const params = { withMeta: 1, ...(this.companyId ? { companyId: this.companyId } : {}) }
        const response = await this.axios.get(
          `${process.env.VUE_APP_API_URL}/users/company-members`,
          { ...this.config, params },
        )
        const data = response?.data || {}
        this.members = Array.isArray(data.members) ? data.members : (Array.isArray(data) ? data : [])
        this.serverAdminCount = typeof data.adminCount === 'number' ? data.adminCount : null
        this.ownerId = data.ownerId || null
        this.owner = data.owner || null
        this.serverCanManageAdminGrants = typeof data.canManageAdminGrants === 'boolean' ? data.canManageAdminGrants : null
      } catch (error) {
        this.loadError = this.errorMessage(error, 'Unable to load people for this company.')
      } finally {
        this.isLoading = false
      }
    },
    formatName(member) {
      const name = `${member?.firstName || ''} ${member?.lastName || ''}`.trim()
      return name || member?.email || '—'
    },
    isSelf(member) {
      return Boolean(this.user) && (member.id === this.user.id || member._id === this.user._id)
    },
    // Locks the switch on the only remaining usable admin. (If the only usable admin is hidden
    // from this list, e.g. unverified, the server still refuses with 409 and that is shown.)
    isLastAdmin(member) {
      return member.isAdmin === true && member.isAccountActivated !== false && this.adminCount <= 1
    },
    isOwner(member) {
      return member?.isTenantOwner === true
    },
    // Why this row's Admin switch is locked (shown as its tooltip), or null when it is editable.
    adminLockReason(member) {
      if (this.isOwner(member)) {
        return 'The tenant owner is always an admin. Ask Teamwrkr to transfer ownership first.'
      }
      if (!this.canManageAdminGrants) {
        return this.hasOwner
          ? 'Only the tenant owner (or Teamwrkr) can make people admins or remove admin.'
          : 'No tenant owner — ask Teamwrkr to change who is an admin.'
      }
      if (this.isLastAdmin(member)) {
        return 'The company needs at least one admin. Add another admin before removing this one.'
      }
      return null
    },
    isBusy(member) {
      return this.busyIds.includes(member.id)
    },
    errorMessage(error, fallback) {
      const status = error?.response?.status
      const message = error?.response?.data?.message
      const code = error?.response?.data?.code
      if (code === 'owner_only') return 'Only the tenant owner (or Teamwrkr) can make people admins or remove admin.'
      if (code === 'owner_cannot_be_demoted') return 'The tenant owner is always an admin. Ask Teamwrkr to transfer ownership first.'
      if (status === 409) {
        return message === 'company needs at least one admin'
          ? 'The company needs at least one admin. Add another admin before removing this one.'
          : message || 'That change conflicts with the current state. Refresh and try again.'
      }
      if (status === 403) return message || "You don't have permission to change access for this person."
      return message || fallback
    },
    async updateMember(member, path, body, applyResult) {
      this.actionError = null
      this.busyIds = [...this.busyIds, member.id]
      try {
        const response = await this.axios.patch(
          `${process.env.VUE_APP_API_URL}/users/${member.id}/${path}`,
          body,
          this.config,
        )
        applyResult(response?.data || {})
        return true
      } catch (error) {
        const message = this.errorMessage(error, 'Unable to update access. Please try again.')
        // Re-sync with the server so the switch shows the real state; fetchMembers resets
        // nothing about actionError, and the message is set after it either way.
        await this.fetchMembers()
        this.actionError = message
        return false
      } finally {
        this.busyIds = this.busyIds.filter(id => id !== member.id)
      }
    },
    async setAdmin(member, isAdmin) {
      if (isAdmin === member.isAdmin) return
      const ok = await this.updateMember(member, 'admin', { isAdmin }, (data) => {
        member.isAdmin = typeof data.isAdmin === 'boolean' ? data.isAdmin : isAdmin
        // Keep the usable-admin count in step with the server without a full reload.
        if (typeof this.serverAdminCount === 'number' && member.isAccountActivated !== false) {
          this.serverAdminCount += member.isAdmin ? 1 : -1
        }
      })
      if (ok && !isAdmin && this.isSelf(member)) {
        this.$emit('self-demoted')
      }
    },
    async setFinancialAccess(member, enabled) {
      const permissionLevel = enabled ? 'admin' : 'user'
      if (permissionLevel === member.permissionLevel) return
      await this.updateMember(member, 'permission-level', { permissionLevel }, (data) => {
        member.permissionLevel = data.permissionLevel || permissionLevel
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.admins-access {
  display: flex;
  flex-direction: column;
  gap: $space-4;
}

.admins-access__title {
  font-size: $font-size-lg;
  font-weight: 700;
  color: $color-text-strong;
  margin: 0 0 $space-2;
}

.admins-access__help {
  color: $color-text-muted;
  font-size: $font-size-sm;
  line-height: 1.5;
  margin: 0 0 $space-2;
}

.admins-access__you {
  color: $color-text-subtle;
  font-size: $font-size-xs;
  margin-left: $space-1;
}

.admins-access__message {
  border-radius: $radius-md;
  padding: $space-3 $space-4;
  font-size: $font-size-sm;
  margin-bottom: $space-3;
}

.admins-access__owner-badge {
  display: inline-block;
  margin-left: $space-2;
  padding: 0 $space-2;
  border-radius: $radius-pill;
  background: $color-primary-soft;
  color: $color-primary-dark;
  font-size: $font-size-xs;
  font-weight: 600;
  white-space: nowrap;
}

.admins-access__message--notice {
  background: $color-primary-soft;
  color: $color-primary-dark;
  border: 1px solid rgba($color-primary, 0.3);
}

.admins-access__message--error {
  background: rgba($color-danger, 0.08);
  color: $color-danger;
  border: 1px solid rgba($color-danger, 0.3);
}
</style>
